import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";
import {
  FieldState,
  GetTemplateFieldStateResponseSchema,
} from "../models/workPaperTemplate";
import { useAudit } from "../contexts/auditContext";
import { workPaperTemplateApiBase } from "../lib/workPaperTemplateApi";

interface UseTemplateFieldStateOptions {
  fetchOnLoad?: boolean;
}

export function useTemplateFieldState(
  sessionId: string | null,
  templateId: string | null,
  options: UseTemplateFieldStateOptions = {},
) {
  const { fetchOnLoad = true } = options;
  const { sessionKind } = useAudit();

  const [loading, setLoading] = useState(false);
  const [fieldStates, setFieldStates] = useState<FieldState[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchFieldStates = useCallback(async () => {
    if (!sessionId || !templateId) {
      setFieldStates([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetchFastApi({
        path: `${workPaperTemplateApiBase(sessionKind, sessionId, templateId)}/state`,
        method: "GET",
      });

      const parsed = GetTemplateFieldStateResponseSchema.parse(response);
      setFieldStates(parsed.fields);
    } catch (err) {
      console.error("Error fetching template field state:", err);
      const errorMessage = "Failed to load template field state";
      setError(errorMessage);
      toast.error(errorMessage);
      setFieldStates([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, templateId, sessionKind]);

  useEffect(() => {
    if (fetchOnLoad && sessionId && templateId) {
      fetchFieldStates();
    }
  }, [fetchOnLoad, sessionId, templateId, fetchFieldStates]);

  return {
    loading,
    fieldStates,
    error,
    refetch: fetchFieldStates,
    setFieldStates,
  };
}
